// Formulaire de création / édition d'une tâche
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { UserAvatar } from "@/components/UserAvatar";
import { PRIORITES, STATUTS_TACHE } from "@/lib/constants";

export const TaskDialog = ({ open, onClose, tache, users, onSaved }) => {
  const [form, setForm] = useState(null);
  const [publications, setPublications] = useState([]);
  const [boards, setBoards] = useState([]);
  const [boardChoisi, setBoardChoisi] = useState("");
  const [cartes, setCartes] = useState([]);

  useEffect(() => {
    if (!open) return;
    setForm(
      tache
        ? { ...tache }
        : { titre: "", description: "", assignes: [], echeance: "", priorite: "normale", statut: "a_faire", lien_type: null, lien_id: null }
    );
    api.get("/editorial-posts").then((r) => setPublications(r.data)).catch(() => {});
    api.get("/boards").then((r) => setBoards(r.data)).catch(() => {});
  }, [open, tache]);

  useEffect(() => {
    if (!boardChoisi) return;
    api.get(`/boards/${boardChoisi}`).then((r) => setCartes(r.data.cards)).catch(() => {});
  }, [boardChoisi]);

  if (!form) return null;

  const set = (champ, valeur) => setForm((f) => ({ ...f, [champ]: valeur }));
  const basculerAssigne = (id) =>
    set("assignes", form.assignes.includes(id) ? form.assignes.filter((x) => x !== id) : [...form.assignes, id]);

  const sauver = async (e) => {
    e.preventDefault();
    const payload = {
      titre: form.titre, description: form.description, assignes: form.assignes,
      echeance: form.echeance || null, priorite: form.priorite, statut: form.statut,
      lien_type: form.lien_type, lien_id: form.lien_id,
    };
    try {
      if (tache) {
        await api.patch(`/tasks/${tache.id}`, payload);
        toast.success("Tâche mise à jour");
      } else {
        await api.post("/tasks", payload);
        toast.success("Tâche créée");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const supprimer = async () => {
    try {
      await api.delete(`/tasks/${tache.id}`);
      toast.success("Tâche supprimée");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{tache ? "Modifier la tâche" : "Nouvelle tâche"}</DialogTitle>
          <DialogDescription>Assignation, échéance, priorité et statut de la tâche.</DialogDescription>
        </DialogHeader>
        <form onSubmit={sauver} className="space-y-4">
          <div>
            <Label>Titre *</Label>
            <Input required value={form.titre} onChange={(e) => set("titre", e.target.value)} className="rounded-xl mt-1.5" data-testid="task-titre-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl mt-1.5" data-testid="task-description-input" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Échéance</Label>
              <Input type="date" value={form.echeance || ""} onChange={(e) => set("echeance", e.target.value)} className="rounded-xl mt-1.5" data-testid="task-echeance-input" />
            </div>
            <div>
              <Label>Priorité</Label>
              <Select value={form.priorite} onValueChange={(v) => set("priorite", v)}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="task-priorite-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Statut</Label>
              <Select value={form.statut} onValueChange={(v) => set("statut", v)}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="task-statut-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUTS_TACHE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Assigné(s)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" className="w-full rounded-xl mt-1.5 justify-start gap-2" data-testid="task-assignes-trigger">
                  <Users className="h-4 w-4" />
                  {form.assignes.length ? users.filter((u) => form.assignes.includes(u.id)).map((u) => u.name).join(", ") : "Choisir des membres"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2">
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-muted cursor-pointer transition-colors">
                    <Checkbox checked={form.assignes.includes(u.id)} onCheckedChange={() => basculerAssigne(u.id)} data-testid={`task-assigne-${u.id}`} />
                    <UserAvatar user={u} size="sm" />
                    <span className="text-sm">{u.name}</span>
                  </label>
                ))}
              </PopoverContent>
            </Popover>
          </div>

          {/* ——— Lien optionnel vers une carte Kanban ou une publication ——— */}
          <div>
            <Label>Lien (optionnel)</Label>
            <div className="grid grid-cols-2 gap-3 mt-1.5">
              <Select
                value={form.lien_type || "aucun"}
                onValueChange={(v) => {
                  set("lien_type", v === "aucun" ? null : v);
                  set("lien_id", null);
                }}
              >
                <SelectTrigger className="rounded-xl" data-testid="task-lien-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aucun">Aucun lien</SelectItem>
                  <SelectItem value="card">Carte Kanban</SelectItem>
                  <SelectItem value="editorial">Publication</SelectItem>
                </SelectContent>
              </Select>
              {form.lien_type === "editorial" && (
                <Select value={form.lien_id || ""} onValueChange={(v) => set("lien_id", v)}>
                  <SelectTrigger className="rounded-xl" data-testid="task-lien-publication-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    {publications.map((p) => <SelectItem key={p.id} value={p.id}>{p.titre}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {form.lien_type === "card" && (
                <Select value={boardChoisi} onValueChange={setBoardChoisi}>
                  <SelectTrigger className="rounded-xl" data-testid="task-lien-board-select"><SelectValue placeholder="Choisir un board…" /></SelectTrigger>
                  <SelectContent>
                    {boards.map((b) => <SelectItem key={b.id} value={b.id}>{b.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            {form.lien_type === "card" && boardChoisi && (
              <Select value={form.lien_id || ""} onValueChange={(v) => set("lien_id", v)}>
                <SelectTrigger className="rounded-xl mt-2" data-testid="task-lien-carte-select"><SelectValue placeholder="Choisir une carte…" /></SelectTrigger>
                <SelectContent>
                  {cartes.map((c) => <SelectItem key={c.id} value={c.id}>{c.titre}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          <DialogFooter className="gap-2">
            {tache && (
              <Button type="button" variant="ghost" onClick={supprimer} className="text-destructive gap-1.5 rounded-full mr-auto" data-testid="task-delete-button">
                <Trash2 className="h-4 w-4" /> Supprimer
              </Button>
            )}
            <Button type="submit" className="rounded-full" data-testid="task-save-button">{tache ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
